/**
 * Collection-graph helpers.
 *
 * Every `SceneObject` belongs to at most one `Collection`. This is a
 * hard invariant — `moveToCollection` and `addToCollection` reroute
 * members through the single-parent rebase path, and this module
 * enforces the invariant at the API boundary.
 *
 * ---------------------------------------------------------------------------
 * World-frame derivation — single source of truth
 * ---------------------------------------------------------------------------
 *
 * A SceneObject's `position`/`rotation`/`scale` are **local to its parent**:
 * the scene root for unattached objects, or the parent `Collection.transform`
 * for collection members. The mesh is drawn at the composed world transform
 * `collection.transform ∘ obj local` — see `PrimitiveRenderer`, which reads
 * it via `getSceneObjectWorldTransform`.
 *
 * Anything that lives OUTSIDE the Three.js scene-graph hierarchy — transform
 * gizmos, operator gizmos, edit-mode overlays, proportional-edit rings, drag
 * math, box-select projection, rip targeting — cannot inherit the composed
 * transform from a parent `<group>`. These consumers MUST derive the world
 * frame through the helpers in this module:
 *
 *   - `getSceneObjectWorldTransform`      — allocating, returns EulerTransform
 *   - `composeSceneObjectWorldTransform`  — scratch, writes pos/quat/scale
 *   - `composeSceneObjectWorldMatrix`     — scratch, writes a Matrix4
 *   - `composeSceneObjectWorldQuaternion` — scratch, writes a Quaternion
 *
 * Direct composition of `obj.position/rotation/scale` into a matrix or
 * quaternion outside this module is a bug: it yields the object's *local*
 * frame, which is offset from the rendered world position by the parent
 * collection's transform. The ECS registry (`buildRegistry`) already reports
 * world transforms for `entity.components.transform`, so object-mode code
 * that reads from the registry is fine; edit-mode code, which reads the
 * SceneObject directly, must go through these helpers.
 */

import * as THREE from "three/webgpu";
import type { SceneObject } from "../modeling/types.ts";
import {
  composeGroupTransform,
  type EulerTransform,
} from "./groupTransform.ts";
import type { AppState, Collection, ModifierOverride } from "./storeTypes.ts";

const _collectionIndexCache = new WeakMap<
  ReadonlyMap<string, Collection>,
  Map<string, string>
>();
const _sceneObjectWorldNameMapCache = new WeakMap<
  ReadonlyMap<string, SceneObject>,
  WeakMap<ReadonlyMap<string, Collection>, Map<string, SceneObject>>
>();

/**
 * Build the `objectId -> collectionId` reverse index from the collections
 * map. Also asserts the one-parent invariant in dev builds: if any scene
 * object is a member of more than one collection, throw loudly so the
 * offending store action is caught in testing. In prod, the second
 * binding is dropped silently to avoid a registry-build crash — the
 * invariant is a bug in a store action, not a data error.
 */
export function buildObjectCollectionIndex(
  collections: ReadonlyMap<string, Collection>,
): Map<string, string> {
  const cached = _collectionIndexCache.get(collections);
  if (cached) return cached;

  const index = new Map<string, string>();
  for (const [collectionId, collection] of collections) {
    for (const objectId of collection.objectIds) {
      const prev = index.get(objectId);
      if (prev !== undefined && prev !== collectionId) {
        if (import.meta.env?.DEV) {
          throw new Error(
            `Collection one-parent invariant violated: scene object "${objectId}" ` +
              `appears in both "${prev}" and "${collectionId}". Store actions that ` +
              `add objects to collections must use moveToCollection / addToCollection ` +
              `so the previous parent is released.`,
          );
        }
        continue;
      }
      index.set(objectId, collectionId);
    }
  }
  _collectionIndexCache.set(collections, index);
  return index;
}

export function findCollectionIdForObject(
  collections: ReadonlyMap<string, Collection>,
  objectId: string,
): string | null {
  return buildObjectCollectionIndex(collections).get(objectId) ?? null;
}

export function getCollectionForObject(
  collections: ReadonlyMap<string, Collection>,
  objectId: string,
): Collection | null {
  const collectionId = findCollectionIdForObject(collections, objectId);
  return collectionId ? (collections.get(collectionId) ?? null) : null;
}

/**
 * Build a name-keyed scene-object map whose collection members carry their
 * composed world transform. Modifier evaluation compares dependency objects
 * against a world-space owner whenever the stack references other scene
 * objects; passing raw collection-local transforms makes Boolean/shrinkwrap
 * targets drift when the collection is moved.
 */
export function buildSceneObjectWorldNameMap(
  sceneObjects: ReadonlyMap<string, SceneObject>,
  collections: ReadonlyMap<string, Collection>,
): Map<string, SceneObject> {
  const cachedByCollection = _sceneObjectWorldNameMapCache.get(sceneObjects);
  const cached = cachedByCollection?.get(collections);
  if (cached) return cached;

  const byName = new Map<string, SceneObject>();
  for (const obj of sceneObjects.values()) {
    const parentCollection = getCollectionForObject(collections, obj.id);
    if (!parentCollection) {
      byName.set(obj.name, obj);
      continue;
    }
    const world = worldTransformForSceneObject(obj, parentCollection);
    byName.set(obj.name, {
      ...obj,
      position: world.position,
      rotation: world.rotation,
      scale: world.scale,
    });
  }

  const nextCachedByCollection =
    cachedByCollection ??
    new WeakMap<ReadonlyMap<string, Collection>, Map<string, SceneObject>>();
  nextCachedByCollection.set(collections, byName);
  if (!cachedByCollection) {
    _sceneObjectWorldNameMapCache.set(sceneObjects, nextCachedByCollection);
  }
  return byName;
}

export type CollectionMemberRemovalStatus = "unchanged" | "updated" | "deleted";

/**
 * Remove members from a cloned collections map and enforce collection
 * lifecycle invariants in one place:
 *
 * - a collection that loses its last member is removed;
 * - a generator-backed collection that loses some, but not all, members is
 *   baked by clearing its generator ref;
 * - cutter metadata tracks the remaining host/cutter membership, and is
 *   cleared if the host or every cutter leaves.
 *
 * Store actions clone `state.collections` before calling this helper; the
 * helper mutates that cloned map so callers cannot forget the empty-collection
 * cleanup after removing members.
 */
export function removeCollectionMembers(
  collections: Map<string, Collection>,
  collectionId: string,
  objectIdsToRemove: Iterable<string>,
): CollectionMemberRemovalStatus {
  const collection = collections.get(collectionId);
  if (!collection) return "unchanged";

  const nextObjectIds = new Set(collection.objectIds);
  let changed = false;
  for (const objectId of objectIdsToRemove) {
    if (nextObjectIds.delete(objectId)) changed = true;
  }
  if (!changed) return "unchanged";

  if (nextObjectIds.size === 0) {
    collections.delete(collectionId);
    return "deleted";
  }

  const {
    cutterRef: _cutterRef,
    generatorRef: _generatorRef,
    ...rest
  } = collection;
  const next: Collection = {
    ...rest,
    objectIds: nextObjectIds,
  };
  const cutterRef = retainedCutterRef(collection, nextObjectIds);
  if (cutterRef) {
    next.cutterRef = cutterRef;
  }
  collections.set(collectionId, next);
  return "updated";
}

function retainedCutterRef(
  collection: Collection,
  objectIds: ReadonlySet<string>,
): Collection["cutterRef"] | undefined {
  const ref = collection.cutterRef;
  if (!ref || !objectIds.has(ref.hostObjectId)) return undefined;
  const cutterObjectIds = new Set<string>();
  for (const objectId of ref.cutterObjectIds) {
    if (objectIds.has(objectId)) cutterObjectIds.add(objectId);
  }
  if (cutterObjectIds.size === 0) return undefined;
  return {
    hostObjectId: ref.hostObjectId,
    cutterObjectIds,
  };
}

/**
 * World-space EulerTransform for a SceneObject given its parent collection
 * (or `null` for scene-root objects). Pure — callers without access to the
 * store maps (edit-op helpers, pivot math) use this; map-based callers use
 * `getSceneObjectWorldTransform` which looks up the collection internally.
 */
export function worldTransformForSceneObject(
  obj: SceneObject,
  parentCollection: Collection | null,
): EulerTransform {
  const local: EulerTransform = {
    position: [obj.position[0], obj.position[1], obj.position[2]],
    rotation: [obj.rotation[0], obj.rotation[1], obj.rotation[2]],
    scale: [obj.scale[0], obj.scale[1], obj.scale[2]],
  };
  return parentCollection
    ? composeGroupTransform(parentCollection.transform, local)
    : local;
}

export function getSceneObjectWorldTransform(
  sceneObjects: ReadonlyMap<string, SceneObject>,
  collections: ReadonlyMap<string, Collection>,
  objectId: string,
): EulerTransform | null {
  if (
    !composeSceneObjectWorldTransform(
      sceneObjects,
      collections,
      objectId,
      _wmPos,
      _wmQuat,
      _wmScale,
    )
  ) {
    return null;
  }
  _wmEuler.setFromQuaternion(_wmQuat);
  return {
    position: [_wmPos.x, _wmPos.y, _wmPos.z],
    rotation: [_wmEuler.x, _wmEuler.y, _wmEuler.z],
    scale: [_wmScale.x, _wmScale.y, _wmScale.z],
  };
}

// Module-local scratch — reused by composeSceneObjectWorld{Transform,Matrix,Quaternion}.
const _wmPos = new THREE.Vector3();
const _wmQuat = new THREE.Quaternion();
const _wmScale = new THREE.Vector3();
const _wmEuler = new THREE.Euler();
const _wmParentQuat = new THREE.Quaternion();
const _wmLocalPos = new THREE.Vector3();
const _wmLocalQuat = new THREE.Quaternion();

/**
 * Compose a scene object's rendered world transform (collection.transform ∘
 * object local) into scratch outputs without allocating. Returns `true` when
 * the object exists, otherwise `false`.
 */
export function composeSceneObjectWorldTransform(
  sceneObjects: ReadonlyMap<string, SceneObject>,
  collections: ReadonlyMap<string, Collection>,
  objectId: string,
  outPosition: THREE.Vector3,
  outQuaternion: THREE.Quaternion,
  outScale: THREE.Vector3,
): boolean {
  const obj = sceneObjects.get(objectId);
  if (!obj) return false;

  const collectionId = findCollectionIdForObject(collections, objectId);
  const parentCollection = collectionId
    ? (collections.get(collectionId) ?? null)
    : null;
  if (!parentCollection) {
    outPosition.set(obj.position[0], obj.position[1], obj.position[2]);
    outQuaternion.setFromEuler(
      _wmEuler.set(obj.rotation[0], obj.rotation[1], obj.rotation[2]),
    );
    outScale.set(obj.scale[0], obj.scale[1], obj.scale[2]);
    return true;
  }

  const parent = parentCollection.transform;
  _wmParentQuat.set(
    parent.rotation[0],
    parent.rotation[1],
    parent.rotation[2],
    parent.rotation[3],
  );
  _wmLocalPos.set(
    parent.scale[0] * obj.position[0],
    parent.scale[1] * obj.position[1],
    parent.scale[2] * obj.position[2],
  );
  outPosition.copy(_wmLocalPos).applyQuaternion(_wmParentQuat);
  outPosition.x += parent.position[0];
  outPosition.y += parent.position[1];
  outPosition.z += parent.position[2];

  _wmLocalQuat.setFromEuler(
    _wmEuler.set(obj.rotation[0], obj.rotation[1], obj.rotation[2]),
  );
  outQuaternion.copy(_wmParentQuat).multiply(_wmLocalQuat);
  outScale.set(
    parent.scale[0] * obj.scale[0],
    parent.scale[1] * obj.scale[1],
    parent.scale[2] * obj.scale[2],
  );
  return true;
}

/**
 * Apply a scene object's rendered world transform directly to an Object3D.
 * Returns the updated object on success, or `null` when the id is missing.
 */
export function applySceneObjectWorldTransformToObject3D(
  sceneObjects: ReadonlyMap<string, SceneObject>,
  collections: ReadonlyMap<string, Collection>,
  objectId: string,
  out: THREE.Object3D,
): THREE.Object3D | null {
  if (
    !composeSceneObjectWorldTransform(
      sceneObjects,
      collections,
      objectId,
      out.position,
      out.quaternion,
      out.scale,
    )
  ) {
    return null;
  }
  return out;
}

/**
 * Compose the world-space matrix for a scene object (collection.transform ∘
 * object local) into the supplied `out` Matrix4. Returns `out` on success, or
 * `null` if the object is not found. Mirrors `getSceneObjectWorldTransform`
 * but avoids allocating an intermediate EulerTransform — intended for
 * per-frame call sites (edit-mode overlays, drag math, box-select projection)
 * where a matrix is what's ultimately needed.
 */
export function composeSceneObjectWorldMatrix(
  sceneObjects: ReadonlyMap<string, SceneObject>,
  collections: ReadonlyMap<string, Collection>,
  objectId: string,
  out: THREE.Matrix4,
): THREE.Matrix4 | null {
  if (
    !composeSceneObjectWorldTransform(
      sceneObjects,
      collections,
      objectId,
      _wmPos,
      _wmQuat,
      _wmScale,
    )
  ) {
    return null;
  }
  out.compose(_wmPos, _wmQuat, _wmScale);
  return out;
}

/**
 * Compose the world-space rotation quaternion for a scene object into the
 * supplied `out` Quaternion. Mirrors `composeSceneObjectWorldMatrix` for the
 * rotation-only consumers (gizmo `<Local>` orientation, handle-axis → command
 * constraint mapping, normal-axis constraint line). Returns `out` on success
 * or `null` if the object is not found.
 *
 * Using the world quaternion (not the SceneObject's raw `rotation`) is
 * required whenever the result is combined with camera/world-space geometry:
 * a child of a rotated Collection has a world rotation of
 * `collection.rotation ∘ child.rotation`, and gizmos rendered in world space
 * must see that composed rotation to align with the rendered mesh.
 */
export function composeSceneObjectWorldQuaternion(
  sceneObjects: ReadonlyMap<string, SceneObject>,
  collections: ReadonlyMap<string, Collection>,
  objectId: string,
  out: THREE.Quaternion,
): THREE.Quaternion | null {
  if (
    !composeSceneObjectWorldTransform(
      sceneObjects,
      collections,
      objectId,
      _wmPos,
      out,
      _wmScale,
    )
  ) {
    return null;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Collection membership validation
// ---------------------------------------------------------------------------

/**
 * Reason an id is rejected as a collection member — used by the store
 * actions to fail loudly at the API boundary instead of silently
 * skipping malformed input or letting incompatible modifiers corrupt
 * the compose math downstream.
 */
export type CollectionMemberRejection =
  | { kind: "not-scene-object"; id: string }
  | { kind: "blend-entry"; id: string }
  | { kind: "nested-collection"; id: string }
  | { kind: "follow-constraint"; id: string };

function hasFollowObjectModifier(
  overrides: readonly ModifierOverride[] | undefined,
): boolean {
  if (!overrides) return false;
  for (const override of overrides) {
    if (override.enabled === false) continue;
    if (override.runtimeModifier?.kind === "followObject") return true;
  }
  return false;
}

/**
 * Return the reason this id cannot become a member of a collection, or
 * `null` when the id is a valid candidate.
 *
 * Rejection rules:
 *  1. **not-scene-object** — the id is not in `state.sceneObjects`. Blend
 *     entries and collection ids surface as a distinct reason code so the
 *     message points at the right misuse (blend entries live in their own
 *     namespace; collections cannot nest).
 *  2. **follow-constraint** — the object has an enabled Follow Object
 *     modifier. Follow constraints resolve in world space; composing a
 *     collection transform on top causes double-translation when the
 *     collection is moved. Out of scope for this refactor per the plan.
 *
 * `import.meta.env?.DEV` callers should throw; prod callers should
 * filter and surface a toast so the user sees *why* the drop failed.
 */
export function rejectCollectionMemberIfInvalid(
  state: Pick<
    AppState,
    "sceneObjects" | "blendFiles" | "collections" | "modifierOverrides"
  >,
  objectId: string,
): CollectionMemberRejection | null {
  if (!state.sceneObjects.has(objectId)) {
    if (state.collections.has(objectId)) {
      return { kind: "nested-collection", id: objectId };
    }
    for (const entry of state.blendFiles.values()) {
      if (entry.file.objects.some((o) => o.name === objectId)) {
        return { kind: "blend-entry", id: objectId };
      }
    }
    return { kind: "not-scene-object", id: objectId };
  }
  if (hasFollowObjectModifier(state.modifierOverrides.get(objectId))) {
    return { kind: "follow-constraint", id: objectId };
  }
  return null;
}

/** Human-readable explanation for a rejection, suitable for toast / throw. */
export function describeCollectionMemberRejection(
  rejection: CollectionMemberRejection,
): string {
  switch (rejection.kind) {
    case "not-scene-object":
      return `"${rejection.id}" is not a scene object and cannot be a collection member.`;
    case "blend-entry":
      return `"${rejection.id}" is a blend-file object. Blend entries are grouped by their own entry transform and cannot be added to collections — their local positions are immutable.`;
    case "nested-collection":
      return `"${rejection.id}" is a collection. Nested collections are out of scope — buildRegistry composes one level only.`;
    case "follow-constraint":
      return `"${rejection.id}" has a Follow Object modifier. Follow constraints resolve in world space, so composing a collection transform on top double-translates the object when the collection moves.`;
  }
}
